/**
 * Product Gallery - Image and Training Video Gallery
 * 
 * Handles product image thumbnails and training video thumbnails,
 * allowing users to switch between product images and embedded training videos.
 */

class ProductGallery {
  constructor(container) {
    this.container = container;
    this.mainImage = container.querySelector('.rr-product-media__image');
    this.thumbnailContainer = container.querySelector('.rr-product-thumbnails-container');
    this.offcanvasElement = document.getElementById('image-offcanvas');
    
    if (!this.mainImage || !this.thumbnailContainer) {
      console.warn('Product gallery: Required elements not found');
      return;
    }

    this.productThumbnails = container.querySelectorAll('.rr-product-thumbnail');
    this.trainingThumbnails = container.querySelectorAll('.rr-training-thumbnail');
    
    // Initialize Bootstrap offcanvas
    this.offcanvas = this.offcanvasElement ? new bootstrap.Offcanvas(this.offcanvasElement) : null;
    
    // Carousel will be initialized after offcanvas is shown
    this.carouselElement = document.getElementById('productImageCarousel');
    this.carousel = null;
    
    this.currentImageIndex = 0;
    this.originalImageHtml = this.mainImage.innerHTML;

    this.init();
  }

  init() {
    // Bind event listeners
    this.bindEvents();

    console.log('Product gallery initialized:', {
      productThumbnails: this.productThumbnails.length,
      trainingThumbnails: this.trainingThumbnails.length
    });
  }

  bindEvents() {
    // Thumbnail click handler (event delegation for both product and training thumbnails)
    this.thumbnailContainer.addEventListener('click', (e) => {
      const productThumb = e.target.closest('.rr-product-thumbnail');
      const trainingThumb = e.target.closest('.rr-training-thumbnail');

      if (productThumb) {
        e.preventDefault();
        this.handleProductThumbnailClick(productThumb);
      } else if (trainingThumb) {
        e.preventDefault();
        this.handleTrainingThumbnailClick(trainingThumb);
      }
    });

    // Main image click - open offcanvas (only for product images)
    this.mainImage.addEventListener('click', () => {
      if (this.mainImage.dataset.contentType === 'image') {
        const currentIndex = parseInt(this.mainImage.dataset.imageIndex) || 0;
        this.openOffcanvas(currentIndex);
      }
    });

    // Offcanvas events
    if (this.offcanvasElement) {
      this.bindOffcanvasEvents();
    }
  }

  handleProductThumbnailClick(thumbnail) {
    const imageUrl = thumbnail.dataset.imageUrl;
    const imageIndex = parseInt(thumbnail.dataset.imageIndex);

    console.log('Product thumbnail clicked:', imageUrl, imageIndex);
    this.showProductImage(imageUrl, imageIndex);
    this.currentImageIndex = imageIndex;

    // Update active states
    this.productThumbnails.forEach((thumb) => thumb.classList.remove('active'));
    this.trainingThumbnails.forEach((thumb) => thumb.classList.remove('active'));
    thumbnail.classList.add('active');
  }

  handleTrainingThumbnailClick(thumbnail) {
    const videoId = thumbnail.dataset.videoId;
    const articleTitle = thumbnail.dataset.articleTitle;
    const articleImage = thumbnail.dataset.articleImage;
    const articleUrl = thumbnail.dataset.articleUrl;

    console.log('Training thumbnail clicked:', articleTitle, videoId);
    this.showTrainingVideo(videoId, articleTitle, articleImage, articleUrl);

    // Update active states
    this.productThumbnails.forEach((thumb) => thumb.classList.remove('active'));
    this.trainingThumbnails.forEach((thumb) => thumb.classList.remove('active'));
    thumbnail.classList.add('active');
  }

  showProductImage(imageUrl, imageIndex) {
    // Restore original structure
    this.mainImage.innerHTML = this.originalImageHtml;
    
    const mainImg = this.mainImage.querySelector('img');
    if (mainImg && imageUrl) {
      mainImg.src = imageUrl;
      if (mainImg.srcset) {
        mainImg.srcset = imageUrl;
      }
    }

    // Remove article button if exists (from training videos)
    const button = this.mainImage.querySelector('.rr-product-media__article-button');
    if (button) {
      button.remove();
    }

    // Update data attributes
    this.mainImage.dataset.imageIndex = imageIndex;
    this.mainImage.dataset.contentType = 'image';
  }

  showTrainingVideo(videoId, articleTitle, articleImage, articleUrl) {
    let content = '';
    
    if (videoId) {
      // YouTube video embed with minimal, clean UI
      // Parameters explanation:
      // - rel=0: Only show related videos from same channel
      // - modestbranding=1: Remove YouTube logo from controls
      // - iv_load_policy=3: Hide video annotations
      // - color=white: White progress bar (cleaner than red)
      // - playsinline=1: Better mobile experience
      // - fs=1: Allow fullscreen
      content = `
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&iv_load_policy=3&color=white&playsinline=1&fs=1"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
        ></iframe>
      `;
    } else if (articleImage) {
      // Article image fallback
      content = `<img src="${articleImage}" alt="${articleTitle}" style="width: 100%; height: 100%; object-fit: contain;">`;
    }

    // Clear main image and add new content
    this.mainImage.innerHTML = content;

    // Add "View Full Article" button
    const button = document.createElement('a');
    button.href = articleUrl;
    button.className = 'rr-product-media__article-button';
    button.innerHTML = `
      <span>View Full Article</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    `;
    this.mainImage.appendChild(button);

    // Update data attributes
    this.mainImage.dataset.contentType = 'training';
  }

  // Offcanvas functions
  bindOffcanvasEvents() {
    if (!this.offcanvasElement) return;

    // Navigate to selected image when offcanvas is fully shown
    this.offcanvasElement.addEventListener('shown.bs.offcanvas', () => {
      if (this.carouselElement && this.currentImageIndex > 0) {
        // Get the carousel instance
        const carousel = bootstrap.Carousel.getInstance(this.carouselElement);
        
        if (carousel) {
          console.log('Navigating carousel to index:', this.currentImageIndex);
          // Use a small delay to ensure carousel is fully ready
          setTimeout(() => {
            carousel.to(this.currentImageIndex);
          }, 100);
        }
      }
    });
  }

  openOffcanvas(index) {
    if (!this.offcanvas || !this.carouselElement) return;
    
    // Store the index to navigate to
    this.currentImageIndex = index;
    
    // Initialize carousel before showing offcanvas
    const carousel = bootstrap.Carousel.getOrCreateInstance(this.carouselElement, {
      interval: false,  // Don't auto-advance
      keyboard: true,   // Enable keyboard navigation
      wrap: true,       // Allow wrapping
      touch: true       // Enable touch/swipe support
    });
    
    // Show the offcanvas
    this.offcanvas.show();
  }

  closeOffcanvas() {
    if (!this.offcanvas) return;
    this.offcanvas.hide();
  }
}

// Initialize all product galleries on the page
function initProductGalleries() {
  const galleries = document.querySelectorAll('[data-product-gallery]');
  
  galleries.forEach((galleryContainer) => {
    new ProductGallery(galleryContainer);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductGalleries);
} else {
  initProductGalleries();
}
